import React from 'react';
import ResponsiveOverlay from '@/components/ResponsiveOverlay/ResponsiveOverlay';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { isNativePlatform } from '@/lib/platform';
import type { PushOptInEntryPoint } from '@/lib/pushOptInTypes';
import './PushPermissionPrompt.css';

export interface PushPermissionPromptProps {
  isOpen: boolean;
  entryPoint: PushOptInEntryPoint;
  onClose: () => void;
  onEnable: () => void;
  isEnabling?: boolean;
}

const PushPermissionPrompt: React.FC<PushPermissionPromptProps> = ({
  isOpen,
  entryPoint,
  onClose,
  onEnable,
  isEnabling = false,
}) => {
  const isMobileViewport = useIsMobileViewport();
  const isMobileSheetLayout = isMobileViewport || isNativePlatform();

  if (!isOpen) return null;

  const promptContent = (
    <div className="push-permission-prompt">
      <button
        type="button"
        className="push-permission-prompt-close"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>
      <h2 className="push-permission-prompt-title">Push notifications</h2>
      <p className="push-permission-prompt-body">
        When someone responds to your post or likes your recommendation, we&apos;ll let you know!
        We won&apos;t do this any more than necessary — like you, we hate getting push
        notifications we don&apos;t want.
      </p>
      <button
        type="button"
        className="push-permission-prompt-enable"
        onClick={onEnable}
        disabled={isEnabling}
      >
        {isEnabling ? 'Enabling…' : 'Enable Push Notifications'}
      </button>
      <button
        type="button"
        className="push-permission-prompt-dismiss"
        onClick={onClose}
        disabled={isEnabling}
      >
        Not now
      </button>
      <span className="sr-only" data-entry-point={entryPoint} />
    </div>
  );

  return (
    <ResponsiveOverlay
      presentation="sheet"
      onClose={onClose}
      forceMobileSheet={isMobileSheetLayout}
      unwrapped={isMobileSheetLayout}
      showSheetHandle={false}
      panelClassName={isMobileSheetLayout ? undefined : 'push-permission-prompt-panel'}
      aria-label="Enable push notifications"
    >
      {isMobileSheetLayout ? (
        <div
          className="push-permission-prompt-drawer"
          onClick={(e) => e.stopPropagation()}
        >
          {promptContent}
        </div>
      ) : (
        <div onClick={(e) => e.stopPropagation()}>{promptContent}</div>
      )}
    </ResponsiveOverlay>
  );
};

export default PushPermissionPrompt;
